import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteOfferCalculationAsync,
  getOfferCalculationAsync,
} from "../../../../slices/offer-calculation";
import { ArrowPathIcon, CalculatorIcon, ClockIcon, Cog6ToothIcon, TrashIcon } from "@heroicons/react/24/outline";
import { dispatchWithToast } from "../../../../components/Utils";
import ProConfirmation from "../../../../components/utils/ProConfirmation";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { OfferCalculationInfo } from "../../../../components/dashboard/offers/calculations/calculation-info";
import CalculationVersionHistoryDialog from "../../../../components/dashboard/offers/calculations/calculation-version-history";
import CalculationCreateDialog from "../../../../components/dashboard/offers/calculations/calculation-create-dialog";
import {
  isOfferCalculationV2Enabled,
  isOfferCalculationTemplateVersionsEnabled,
  resolveCalculationContributionMargin,
  resolveCalculationContributionMarginPercent,
  resolveCalculationCostTotal,
  resolveCalculationSalesTotal,
  resolveOfferCalculationFeatureFlags,
} from "../../../../components/dashboard/offers/utils";
import "./calculation.css";

const moneyFmt = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  maximumFractionDigits: 0,
});

const percentFmt = new Intl.NumberFormat("nb-NO", {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

export const OfferCalculation = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const itemState = {
    ...useSelector((store) => {
      return store.offerCalculations;
    }),
  };
  const { isLoading, error, errors, calculation } = itemState;
  const offerFeatureFlags = useSelector(
    (store) => store.offers?.offerFeatureFlags || {}
  );
  const offer = useSelector((store) => store.offers?.offer || {});

  const featureFlags = resolveOfferCalculationFeatureFlags({
    calculation,
    offer,
    offerFeatureFlags,
  });
  const calculationV2Enabled = isOfferCalculationV2Enabled(featureFlags);
  const templateVersionsEnabled = isOfferCalculationTemplateVersionsEnabled(featureFlags);
  const isTemplate = calculation?.id && calculation?.offer_id == null;

  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id && id !== "new") {
      dispatch(getOfferCalculationAsync(id));
    }
  }, [dispatch, id]);

  // /new is create-dialog-first — never drop into a blank builder shell.
  if (id === "new") {
    return (
      <CalculationCreateDialog
        open
        onClose={() => navigate("/dashboard/offers/calculations")}
      />
    );
  }

  const handleDeletePrompt = () => {
    setDeletePromptOpen(true);
  };

  const handleAfterDispatch = () => {
    setDeletePromptOpen(false);
    navigate("/dashboard/offers/calculations");
  };

  const handleDeleteItem = () => {
    dispatchWithToast(dispatch(deleteOfferCalculationAsync(calculation?.id)), null, () =>
      handleAfterDispatch(false)
    );
  };

  const totalAmount =
    resolveCalculationSalesTotal(calculation, { v2: calculationV2Enabled }) ?? 0;
  const costTotal = resolveCalculationCostTotal(calculation, {
    v2: calculationV2Enabled,
  });
  const contributionMargin = calculationV2Enabled
    ? resolveCalculationContributionMargin(calculation)
    : null;
  const contributionMarginPercent = calculationV2Enabled
    ? resolveCalculationContributionMarginPercent(calculation)
    : null;
  const formattedTotal = moneyFmt.format(
    Number.isFinite(totalAmount) ? totalAmount : 0
  );

  const renderContent = () => {
    if (isLoading && id !== "new") {
      return <ResourceLoading />;
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    return (
      <OfferCalculationInfo
        calculation={calculation}
        isLoading={isLoading}
        calculationV2Enabled={calculationV2Enabled}
        featureFlags={featureFlags}
        settingsOpen={settingsOpen}
        onSettingsOpenChange={setSettingsOpen}
      />
    );
  };

  return (
    <div
      className={[
        "offer-calculation-page",
        calculation?.module_enabled ? "offer-calculation-page--map" : "",
      ].join(" ")}
    >
      <header className="offer-calculation-page__header">
        <div className="offer-calculation-page__header-inner">
          <div>
            <div className="offer-calculation-page__kicker">
              {t("offer.calculation.page.kicker")}
            </div>
            <h1 className="offer-calculation-page__title">
              {calculation?.id
                ? calculation?.title || t("offer.calculation.untitled")
                : id === "new"
                ? t("offer.calculation.new")
                : t("offer.calculation.untitled")}
            </h1>
            <p className="offer-calculation-page__subtitle">
              {t("offer.calculation.page.subtitle")}
            </p>
            <div className="offer-calculation-page__meta">
              <span className="offer-calculation-page__pill is-accent">
                <CalculatorIcon className="h-4 w-4" />
                {formattedTotal}
              </span>
              {calculationV2Enabled && costTotal != null ? (
                <span className="offer-calculation-page__pill">
                  {t("offer.calculation.totals.cost_total")}: {moneyFmt.format(costTotal)}
                </span>
              ) : null}
              {calculationV2Enabled && contributionMargin != null ? (
                <span className="offer-calculation-page__pill">
                  {t("offer.calculation.totals.contribution_margin")}:{" "}
                  {moneyFmt.format(contributionMargin)}
                  {contributionMarginPercent != null
                    ? ` (${percentFmt.format(contributionMarginPercent)}%)`
                    : ""}
                </span>
              ) : null}
              {calculationV2Enabled ? (
                <span className="offer-calculation-page__pill">
                  {t("offer.calculation.totals.authoritative_hint")}
                </span>
              ) : null}
              <span className="offer-calculation-page__pill">
                {calculation?.active === false ? t("offer.calculation.inactive") : t("offer.calculation.active")}
              </span>
              <span className="offer-calculation-page__pill">
                {calculation?.module_enabled ? t("offer.calculation.builder.tab_modules") : t("offer.calculation.builder.tab_lines")}
              </span>
            </div>
          </div>
          <div className="offer-calculation-page__actions">
            <button
              className="offer-calculation-page__button"
              onClick={() => setSettingsOpen(true)}
              type="button"
            >
              <Cog6ToothIcon className="h-4 w-4" />
              {t("offer.calculation.settings.open")}
            </button>
            {id !== "new" && templateVersionsEnabled && isTemplate ? (
              <button
                className="offer-calculation-page__button"
                disabled={isLoading}
                onClick={() => setVersionHistoryOpen(true)}
                type="button"
              >
                <ClockIcon className="h-4 w-4" />
                {t("offers.calculation.versions.title")}
              </button>
            ) : null}
            {id !== "new" ? (
              <button
                className="offer-calculation-page__button"
                disabled={isLoading}
                onClick={() => dispatch(getOfferCalculationAsync(id))}
                type="button"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t("Refresh")}
              </button>
            ) : null}
            {id !== "new" ? (
              <button
                className="offer-calculation-page__button is-danger"
                disabled={isLoading}
                onClick={() => handleDeletePrompt(calculation)}
                type="button"
              >
                <TrashIcon className="h-4 w-4" />
                {t("offer.calculation.delete")}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="offer-calculation-page__content">
        {renderContent()}
      </main>

      <ProConfirmation
        severity="confirmation"
        open={deletePromptOpen}
        message={"Denne kalkylen vil da bli fjernet for godt og kan ikke reverseres"}
        action="delete"
        module="offer_calculation"
        yesCallback={() => handleDeleteItem()}
        cancelCallback={() => setDeletePromptOpen(false)}
      />

      <CalculationVersionHistoryDialog
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        calculationId={calculation?.id}
        onRestored={() => dispatch(getOfferCalculationAsync(id))}
      />
    </div>
  );
};



